import moment from 'moment-timezone';
import { mapRestriktionstyp, getRestriktionsgrundLabel } from './restriktionen.constants.js';
import { germanCountryName } from '@/core/common/iso3166Countries.js';

// Einheitliches Format
export const DAY_KEY_FORMAT = 'YYYY-MM-DD';
export const WOCHENTAGE_OPTIONS = [
  { value: 'Monday', kurz: 'Mo', lang: 'Montag' },
  { value: 'Tuesday', kurz: 'Di', lang: 'Dienstag' },
  { value: 'Wednesday', kurz: 'Mi', lang: 'Mittwoch' },
  { value: 'Thursday', kurz: 'Do', lang: 'Donnerstag' },
  { value: 'Friday', kurz: 'Fr', lang: 'Freitag' },
  { value: 'Saturday', kurz: 'Sa', lang: 'Samstag' },
  { value: 'Sunday', kurz: 'So', lang: 'Sonntag' },
];

export const weekdaysLong = WOCHENTAGE_OPTIONS.map(option => option.lang);
export const weekdaysShort = WOCHENTAGE_OPTIONS.map(option => option.kurz);
const WOCHENTAG_LABEL = WOCHENTAGE_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.lang;
  return acc;
}, {});

export const monthNames = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

// Monatsname für ein Datum zurückgegeben
export function monthLabel(date) {
  const monat = moment(date);
  return `${monthNames[monat.month()]} ${monat.year()}`;
}

// Datum als „11. Mai 2026" formatiert
export function formatDatumLang(date) {
  const monat = moment(date);
  return `${monat.date()}. ${monthNames[monat.month()]} ${monat.year()}`;
}

// Tag mit vorangestelltem Wochentag formatiert
// Standard „Montag, 19.05.2026", mit lang „Dienstag, 19. Mai 2026"
export function formatWochentagDatum(date, { lang = false } = {}) {
  const m = moment(date);
  const datum = lang ? formatDatumLang(m) : m.format('DD.MM.YYYY');
  return `${weekdaysLong[m.isoWeekday() - 1]}, ${datum}`;
}

const UNBEFRISTET_JAHR = 9999;

// Serienende mit unbefristeter Serie
export function istUnbefristet(serieBis) {
  return !serieBis || moment(serieBis).year() >= UNBEFRISTET_JAHR;
}

// Start und Ende einer Restriktion ermittelt
// dauer zählt den Starttag mit, das Ende einer einmaligen Sperrzeit liegt daher bei startdatum + dauer - 1
// ende ist null, wenn die einmalige Sperrzeit nur einen Tag umfasst oder die Serie unbefristet läuft
export function getRestriktionZeitraum(entry) {
  const start = moment(entry.startdatum);
  const istSingle = entry.wiederholungstyp === 'Single';
  if (istSingle) {
    const ende = entry.dauer > 1 ? start.clone().add(entry.dauer - 1, 'days') : null;
    return { start, ende, istSingle };
  }
  const ende = istUnbefristet(entry.serieBis) ? null : moment(entry.serieBis);
  return { start, ende, istSingle };
}

// Zeitraum einer Restriktion als Satzteil formatiert
// Einmalig: „am 11.05.2026" bzw. „vom 11.05.2026 bis 31.05.2026"
// Serie: „vom 11.05.2026 bis 31.08.2026" bzw. „ab 11.05.2026 (unbefristet)"
export function formatZeitraumText(entry, { lang = false, betonen = false } = {}) {
  const { start, ende, istSingle } = getRestriktionZeitraum(entry);
  const datumText = datum => {
    const text = lang ? formatDatumLang(datum) : moment(datum).format('DD.MM.YYYY');
    return betonen ? `<strong>${text}</strong>` : text;
  };

  if (ende) {
    return `vom ${datumText(start)} bis ${datumText(ende)}`;
  }
  return istSingle ? `am ${datumText(start)}` : `ab ${datumText(start)} (unbefristet)`;
}

// Zeitraum in Kurzform für kompakte Auflistungen
export function formatZeitraumKompakt(entry) {
  const { start, ende, istSingle } = getRestriktionZeitraum(entry);
  if (ende) {
    const startFormat = start.year() === ende.year() ? 'DD.MM.' : 'DD.MM.YYYY';
    return `${start.format(startFormat)} – ${ende.format('DD.MM.YYYY')}`;
  }
  return `${istSingle ? 'nur am' : 'ab'} ${start.format('DD.MM.YYYY')}`;
}

// Ordinalzahlen für das n-te Vorkommen im Monat
const ORDINAL_LABEL = { 1: 'ersten', 2: 'zweiten', 3: 'dritten', 4: 'vierten', 5: 'fünften' };

// Optionen für das n-te Vorkommen eines Wochentags im Monat (Backend erlaubt 1 bis 4)
export const NTES_VORKOMMEN_OPTIONS = [
  { value: 1, text: 'Erster' },
  { value: 2, text: 'Zweiter' },
  { value: 3, text: 'Dritter' },
  { value: 4, text: 'Vierter' },
];

// Wochentage als Aufzählung formatiert (z. B. „Montag und Mittwoch")
function formatWochentage(wochentage = []) {
  const namen = (wochentage || []).map(tag => WOCHENTAG_LABEL[tag] || tag);
  if (namen.length <= 1) {
    return namen[0] || '';
  }
  return `${namen.slice(0, -1).join(', ')} und ${namen[namen.length - 1]}`;
}

// Serienbeschreibung einer wiederkehrenden Restriktion erzeugt
// Weekly: „jeden {Tage}" bzw. „jede {turnus}. Woche am {Tage}"
// Monthly: „am {Ordinal} {Tag} im Monat" bzw. „… alle {turnus} Monate"
export function formatSerienBeschreibung(entry) {
  const turnus = entry.turnus || 1;
  const tage = formatWochentage(entry.wochentage);

  if (entry.wiederholungstyp === 'Weekly') {
    if (!tage) {
      return '';
    }
    return turnus > 1 ? `jede ${turnus}. Woche am ${tage}` : `jeden ${tage}`;
  }

  if (entry.wiederholungstyp === 'Monthly') {
    if (!tage) {
      return '';
    }
    const n = entry.ntesVorkommenImMonat;
    const ordinal = n < 0 ? 'letzten' : ORDINAL_LABEL[n] || `${n}.`;
    const kern = `am ${ordinal} ${tage}`;
    return turnus > 1 ? `${kern} alle ${turnus} Monate` : `${kern} im Monat`;
  }

  return '';
}

// die Zusammenfassung für den Hinweis-Banner im Sperrzeit-Dialog erzeugt
// Einmalig: „Einmalig am 11. Mai 2026." bzw. „Einmalig vom 11. Mai 2026 bis 31. Mai 2026."
// Wöchentlich: „Jede Woche am Montag, Mittwoch und Freitag ab 11. Mai 2026 bis 31. August 2026."
// Monatlich: „Jeden Monat am 11. ab 11. Mai 2026." bzw. „Jeden Monat am 2. Samstag ab 11. Mai 2026."
// Jährlich: „Jedes Jahr am 11. Mai ab 2026."
export function formatSperrzeitZusammenfassung({
  tab,
  von,
  bis,
  turnus = 1,
  wochentage = [],
  monatModus,
  ntesVorkommen,
  monatWochentag,
  serieBis,
}) {
  if (!von) {
    return '';
  }
  const start = moment(von);
  const startText = formatDatumLang(start);
  const endeText = serieBis ? ` bis ${formatDatumLang(serieBis)}` : '';

  if (tab === 'einmalig') {
    if (bis && !moment(bis).isSame(start, 'day')) {
      return `Einmalig vom ${startText} bis ${formatDatumLang(bis)}.`;
    }
    return `Einmalig am ${startText}.`;
  }

  if (tab === 'woechentlich') {
    const tage = formatWochentage(wochentage);
    if (!tage) {
      return '';
    }
    const prefix = turnus > 1 ? `Jede ${turnus}. Woche` : 'Jede Woche';
    return `${prefix} am ${tage} ab ${startText}${endeText}.`;
  }

  if (tab === 'monatlich') {
    const prefix = turnus > 1 ? `Alle ${turnus} Monate` : 'Jeden Monat';
    if (monatModus === 'wochentag') {
      const tagName = WOCHENTAG_LABEL[monatWochentag] || monatWochentag;
      return `${prefix} am ${ntesVorkommen}. ${tagName} ab ${startText}${endeText}.`;
    }
    return `${prefix} am ${start.date()}. ab ${startText}${endeText}.`;
  }

  if (tab === 'jaehrlich') {
    const endeJahr = serieBis ? ` bis ${moment(serieBis).year()}` : '';
    return `Jedes Jahr am ${start.date()}. ${monthNames[start.month()]} ab ${start.year()}${endeJahr}.`;
  }

  return '';
}

// die Anzahl der Wochenreihen ermittelt, die ein Monat im Montag-Raster tatsächlich belegt
export function getWochenAnzahl(date) {
  const monat = moment(date);
  const versatz = monat.clone().startOf('month').isoWeekday() - 1;
  return Math.ceil((versatz + monat.daysInMonth()) / 7);
}

// die Wochen-Matrix eines Monats aufgebaut
// Rückgabe ist ein Array von wochenAnzahl Wochen, jede Woche enthält sieben Tagesobjekte
// Die Matrix startet am Montag und füllt angrenzende Monatstage auf
export function buildMonthMatrix(date, wochenAnzahl = 6) {
  const reference = moment(date);
  const today = moment();

  const gridStart = reference.clone().startOf('month').startOf('isoWeek');

  const weeks = [];
  const cursor = gridStart.clone();

  for (let weekIndex = 0; weekIndex < wochenAnzahl; weekIndex += 1) {
    const week = [];
    for (let i = 0; i < 7; i += 1) {
      week.push({
        date: cursor.clone(),
        key: cursor.format(DAY_KEY_FORMAT),
        dayNumber: cursor.date(),
        inCurrentMonth: cursor.isSame(reference, 'month'),
        isToday: cursor.isSame(today, 'day'),
      });
      cursor.add(1, 'day');
    }
    weeks.push(week);
  }

  return weeks;
}

// die Restriktionskonfiguration in flache Tages-Einträge übersetzt
// result enthält die Konfigurationsgruppen laender und reisen, jede Gruppe hält ein Array von gesperrten Datumsangaben
// Pro Datum entsteht ein Eintrag { date, type, grund, kommentar? }, die die Views direkt verarbeiten
export function mapKonfigurationToRestriktionen(result) {
  if (!result) {
    return [];
  }
  const gruppen = [...(result.laender || []), ...(result.reisen || [])];
  return gruppen.flatMap(gruppe => {
    const type = mapRestriktionstyp(gruppe.restriktionstyp, gruppe.wiederholungstyp);
    const grund = getRestriktionsgrundLabel(gruppe.restriktionsgrund);
    // Zugeordnete Reisen und Länder für die Anzeige im Popover, Länder als deutsche Namen
    const reisekuerzel = gruppe.reisekuerzel || [];
    const laender = (gruppe.laender || []).map(code => germanCountryName(code));
    return (gruppe.restriktionen || []).map(datum => {
      const eintrag = {
        date: moment(datum).format(DAY_KEY_FORMAT),
        // Originaler ISO-Zeitstempel des Tages
        dateIso: datum,
        type,
        grund,
        // Zusatzfelder für das Tages-Detail-Modal
        id: gruppe.id,
        restriktionstyp: gruppe.restriktionstyp,
        restriktionsgrund: gruppe.restriktionsgrund,
        laenderCodes: gruppe.laender || [],
        wiederholungstyp: gruppe.wiederholungstyp,
        startdatum: gruppe.startdatum,
        serieBis: gruppe.serieBis,
        dauer: gruppe.dauer,
        wochentage: gruppe.wochentage,
        turnus: gruppe.turnus,
        ntesVorkommenImMonat: gruppe.ntesVorkommenImMonat,
      };
      if (reisekuerzel.length) {
        eintrag.reisekuerzel = reisekuerzel;
      }
      if (laender.length) {
        eintrag.laender = laender;
      }
      if (gruppe.kommentar) {
        eintrag.kommentar = gruppe.kommentar;
      }
      return eintrag;
    });
  });
}

export function toDayKey(date) {
  const d = date instanceof Date ? date : new Date(date);
  const monat = `${d.getMonth() + 1}`.padStart(2, '0');
  const tag = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${monat}-${tag}`;
}

// die Restriktionen nach Tagesschlüssel gruppiert
export function groupRestriktionenByDay(restriktionen = []) {
  return restriktionen.reduce((acc, restriktion) => {
    if (!acc[restriktion.date]) {
      acc[restriktion.date] = [];
    }
    acc[restriktion.date].push(restriktion);
    return acc;
  }, {});
}

// die Anzahl gesperrter Tage eines Monats oder eines Jahres ermittelt
export function countBlockedDays(restriktionen = [], date, einheit = 'month') {
  const praefix = moment(date).format(einheit === 'year' ? 'YYYY' : 'YYYY-MM');
  const blockedDays = new Set();
  restriktionen.forEach(restriktion => {
    if (restriktion.date.startsWith(praefix)) {
      blockedDays.add(restriktion.date);
    }
  });
  return blockedDays.size;
}
